import React, { useState } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemIcon, ListItemText, IconButton, Link } from '@mui/material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import LanguageThemeBar from "../LanguageThemeBar";
import { pdfjs } from 'react-pdf';

export default function ToolUploadPage() {
    const { toolId } = useParams();
    const location = useLocation();
    const toolName = location.state?.toolName || `Tool ${toolId}`;

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const processFiles = async (files) => {
        const pdfFiles = Array.from(files).filter(file => file.type === "application/pdf");
        if (pdfFiles.length !== files.length) {
            alert("Some files were not PDFs and were ignored.");
        }

        // Validate page count for each PDF
        const validFiles = [];
        for (const file of pdfFiles) {
            try {
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
                const pageCount = pdf.numPages;

                if (pageCount > 10) {
                    alert(`"${file.name}" has ${pageCount} pages. Please upload PDFs with maximum 10 pages.`);
                    continue;
                }

                validFiles.push(file);
            } catch (error) {
                console.error(`Error reading PDF ${file.name}:`, error);
                alert(`Error reading "${file.name}". Please try again.`);
            }
        }

        // Generate object URLs for preview links
        const filesWithUrls = validFiles.map(file => ({
            file,
            url: URL.createObjectURL(file),
        }));

        setSelectedFiles(prev => [...prev, ...filesWithUrls]);
    };

    const handleFileChange = async (e) => {
        await processFiles(e.target.files);
        e.target.value = '';
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            await processFiles(event.dataTransfer.files);
        }
    };

    const handleDeleteFile = (index) => {
        // Revoke object URL to avoid memory leaks
        URL.revokeObjectURL(selectedFiles[index].url);
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = () => {
        if (selectedFiles.length === 0) {
            alert("Please select at least one PDF file first.");
            return;
        }

        navigate('/tool-results', {
            state: {
                files: selectedFiles,
                toolId: toolId,
            },
        });
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={4}>
            <Button
                variant="outlined"
                sx={{
                    position: 'fixed',
                    top: 20,
                    left: 20,
                    zIndex: 2500,
                }}
                onClick={() => navigate('/home')}
            >
                {t("homepage")}
            </Button>

            <Box position="absolute" top={10} right={10}>
                <LanguageThemeBar />
            </Box>

            <Typography variant="h4" mb={2}>{toolName}</Typography>
            <Typography variant="body1" mb={3}>{t("use_tool_upload")}</Typography>

            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
                    borderRadius: 2,
                    padding: 4,
                    minWidth: 400,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    mb: 2
                }}
            >
                <Typography variant="body2" color="text.secondary" mb={2}>
                    {isDragging ? t("drop_files_here", "Drop files here") : t("drag_drop_hint", "Drag and drop PDF files here, or")}
                </Typography>
                <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                >
                    {t("select_pdf")}
                    <input
                        type="file"
                        accept="application/pdf"
                        hidden
                        multiple
                        onChange={handleFileChange}
                    />
                </Button>
            </Box>

            {selectedFiles.length > 0 && (
                <>
                    <Typography variant="body1" mb={2}>
                        Selected files:
                    </Typography>
                    <List dense sx={{ width: '100%', maxWidth: 600, mb: 2 }}>
                        {selectedFiles.map(({ file, url }, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteFile(index)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemIcon>
                                    <InsertDriveFileIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Link
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                        >
                                            {file.name}
                                        </Link>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </>
            )}

            <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={selectedFiles.length === 0}
                startIcon={<RocketLaunchIcon />}
                sx={{
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    textAlign: 'center',
                    px: 2,
                }}
            >
                <Box component="span">
                    {t("run_tool")}
                </Box>
            </Button>
        </Box>
    );
}
